import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import ProfessionalCourseApplicationForm from "../components/Courses/ProfessionalCourses/ProfessionalCourseApplicationForm";
import CompTIACertCard from "../components/Courses/ProfessionalCourses/CompTIACertCard";
import comptiaImg from "../assets/comptia.png";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import { parseOverviewJson, slugifyCert } from "../utils/comptiaCerts";

// Single certification page, e.g. /courses/professional-courses/comptia/comptia-a
// Mirrors CompTIA's own site: a real, linkable, breadcrumbed route per certification
// rather than an in-page reveal.
const CompTIACertDetailPage = () => {
  const { certSlug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    cmsApi
      .getProgramBySlug("comptia-certification")
      .then((res) => {
        if (res.success && res.program) setProgram(res.program);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [certSlug]);

  const parsed = parseOverviewJson(program?.comptiaOverviewSection ?? null);
  const cert = parsed?.certs?.find((c) => slugifyCert(c.name) === certSlug) ?? null;

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!cert) {
    return (
      <div>
        <Navbar />
        <div className="mid-desktop-page container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl py-24 text-center">
          <h1 className="text-3xl font-bold text-custom-pink mb-4">Certification not found</h1>
          <p className="text-gray-600 mb-8">
            We couldn&apos;t find that CompTIA certification. It may have been renamed or removed.
          </p>
          <Link
            to="/courses/professional-courses/comptia"
            className="inline-block bg-custom-pink text-white font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
          >
            &larr; Back to Discover Our Certifications
          </Link>
        </div>
        <Footer />
        <FloatingSocialPanels />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
        <CourseHeader
          title="CompTIA"
          highlight={cert.name}
          subtitle={cert.tag || "Vendor-neutral IT certification — from an authorised training partner in Tanzania."}
          backgroundImage={(cert.heroImage || program?.heroImage) ? getImageUrl(cert.heroImage || program.heroImage) : comptiaImg}
          cta={{ label: "Start Application", targetId: "application-form" }}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Professional Courses", href: "/course/professional-courses" },
            { label: "CompTIA", href: "/courses/professional-courses/comptia" },
            { label: cert.name },
          ]}
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl py-16 md:py-24">
          <Link
            to="/courses/professional-courses/comptia"
            className="mb-6 inline-flex items-center gap-2 text-custom-pink font-semibold hover:underline"
          >
            <span aria-hidden="true">&larr;</span> Back to all certifications
          </Link>

          <CompTIACertCard cert={cert} />

          <div id="application-form" className="mt-16">
            <ProfessionalCourseApplicationForm programName={`CompTIA ${cert.name}`} fieldLabel="Professional Course" />
          </div>
        </div>
      </div>
      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default CompTIACertDetailPage;
